import { Error as ErrorIcon, Lock } from '@mui/icons-material';
import { Chip, Grid, Tooltip } from '@mui/material';
import { CustomFieldDefinition, CustomFieldValues } from '@nx-smartmonkey/shared/domain';
import {
  dateFormat,
  parsePhoneNumber,
  parseSecondsToHM,
  parseSecondsToHMText,
  pastDateTime,
} from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';

import More3Points from '../../../../assets/svg/More3Points';
import { RatingStars } from '../../../../components/RatingStars';
import { customFieldToTableValue } from '../../../../components/TableColumnsButton';
import { DriversActionsMenu } from '../../../../components/menus/DriversActionsMenu';
import { CUSTOM_FIELD_COLUMN_ID_PREFIX } from '../../../../constants';
import DRIVER_STATUS from '../../../../constants/Drivers';
import { DriversTableColumn, DriversTableColumnKeys } from '../../../../context/driver/tableColumns';
import { Driver } from '../../../../domain/driver/Driver';
import { Project } from '../../../../domain/project/Project';
import { Stop } from '../../../../domain/stop/Stop';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { kmToSM } from '../../../../helpers/Units';
import { DetailTabTypes } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import Text from '../../../../react-components/Text';
import ProblemsTooltip from '../../shared/ProblemsTooltip';
import {
  AlignmentTypes,
  VirtualizedDataTableColumnProps,
} from '../../shared/virtualized-datatable/VirtualizedDataTable';
import { getDriverStatusColor } from '../StatusBar';

type IColumnsResponse = Record<string, any>[];

type IDriversGetColumns = {
  activeProject: Project;
  showRootParams?: boolean;
  planStops: Array<Stop>;
  planDrivers: Array<Driver>;
  tableColumns: Array<DriversTableColumn>;
  onOpenRightPanel?: (event: any, stop: Driver, detailTab?: DetailTabTypes) => any;
};

export const getDriversColumns = ({
  activeProject,
  showRootParams = false,
  tableColumns,
  planStops,
  planDrivers,
  onOpenRightPanel,
}: IDriversGetColumns): IColumnsResponse => {
  const columns: Array<VirtualizedDataTableColumnProps> = [];

  const driverCustomFieldDefinitionsMap: Record<string, CustomFieldDefinition> = (
    activeProject.custom_fields?.vehicle ?? []
  )
    .filter((customFieldDefinition) => customFieldDefinition.enabled)
    .reduce((amount, customFieldDefinition) => {
      return {
        ...amount,
        [`${CUSTOM_FIELD_COLUMN_ID_PREFIX}${customFieldDefinition.custom_id}`]: customFieldDefinition,
      };
    }, {});

  tableColumns.forEach(({ displayable, identifier, sortable }: DriversTableColumn) => {
    if (displayable) {
      switch (identifier) {
        case `label`:
          columns.push({
            width: 250,
            accessorName: `label`,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planoverview.route.table.header.label" />
            ),
            formatter: ({ doc: driver }) => (
              <Grid container direction="row" alignItems="center">
                {driver.is_locked && (
                  <Tooltip title={<Text variant="body" intlId="planoverview.route.table.header.locked_tooltip" />}>
                    <Lock
                      style={{
                        fontSize: `18px`,
                        marginRight: `4px`,
                        color: RoutalPalette.secondary40,
                      }}
                    />
                  </Tooltip>
                )}

                {driver.label && driver.label !== `` ? (
                  <Tooltip title={driver.label}>
                    <Text variant="body">
                      {driver.label.length > 30 ? `${driver.label.substring(0, 30)}...` : `${driver.label}`}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                )}
              </Grid>
            ),
          });
          break;
        case `alerts`:
          columns.push({
            width: 68,
            accessorName: `alerts`,
            align: `center` as AlignmentTypes,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planoverview.route.table.header.alerts" />
            ),
            formatter: ({ doc: driver }) => (
              <ProblemsTooltip type="driver" driver={driver} tooltipPlacement="top">
                <ErrorIcon style={{ color: `#D0021B`, fontSize: `24px` }} />
              </ProblemsTooltip>
            ),
          });
          break;
        case `survey`:
          if (activeProject?.drivers.hasSurveyEnabled()) {
            columns.push({
              width: 135,
              accessorName: `survey`,
              align: `center` as AlignmentTypes,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId="planoverview.route.table.header.survey"
                />
              ),
              formatter: ({ doc }) => {
                if (!doc.survey) {
                  return null;
                }
                return <RatingStars rating={doc.survey?.rating ?? 0} justifyContent="center" />;
              },
            });
          }
          break;
        case `status`:
          columns.push({
            width: 135,
            accessorName: `status`,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planoverview.route.table.header.status" />
            ),
            formatter: ({ value: status }) =>
              status && (
                <Grid container alignItems="center">
                  <Chip
                    style={{
                      backgroundColor: getDriverStatusColor(status),
                      fontSize: `10px`,
                      width: `120px`,
                    }}
                    label={
                      <Text variant="body" style={{ color: RoutalPalette.white }} intlId={`routes.status.${status}`} />
                    }
                  />
                </Grid>
              ),
          });
          break;
        case `stops`:
          columns.push({
            width: 80,
            accessorName: `stops`,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planoverview.route.table.header.stops" />
            ),
            formatter: ({ doc }) => <Text variant="body">{doc.stops.length}</Text>,
          });
          break;
        case `start_location`:
          columns.push({
            width: 240,
            accessorName: `start_location`,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.start_location"
              />
            ),
            formatter: ({ doc }) =>
              doc.start_location && (
                <Tooltip title={doc.start_location?.label ?? ``}>
                  <Text variant="body" style={{ maxWidth: `240px` }}>
                    {doc.start_location.label}
                  </Text>
                </Tooltip>
              ),
          });
          break;
        case `end_location`:
          columns.push({
            width: 240,
            accessorName: `end_location`,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.end_location"
              />
            ),
            formatter: ({ doc }) =>
              doc.end_location && (
                <Tooltip title={doc.end_location?.label ?? ``}>
                  <Text variant="body" style={{ maxWidth: `240px` }}>
                    {doc.end_location.label}
                  </Text>
                </Tooltip>
              ),
          });
          break;
        case `time_window`:
          if (activeProject?.view?.constraints?.time_windows) {
            columns.push({
              width: 135,
              accessorName: `time_window`,
              align: `center` as AlignmentTypes,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId="planoverview.route.table.header.time_window"
                />
              ),
              formatter: ({ value: doc }) => {
                if (doc && doc.length > 0) {
                  return (
                    <Chip
                      key={doc[0] + doc[1]}
                      label={<Text variant="body">{`${parseSecondsToHM(doc[0])}-${parseSecondsToHM(doc[1])}`}</Text>}
                    />
                  );
                }
                return null;
              },
            });
          }
          break;
        case `duration`:
          columns.push({
            width: 110,
            accessorName: `duration`,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.route_time"
              />
            ),
            formatter: ({ doc }) => (
              <Text variant="body">
                {doc.planned_end_time !== undefined && doc.planned_start_time !== undefined
                  ? `${parseSecondsToHMText(doc.planned_end_time - doc.planned_start_time)}`
                  : ``}
              </Text>
            ),
          });
          break;

        case `total_distance`:
          columns.push({
            width: 120,
            accessorName: `total_distance`,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text style={{ color: RoutalPalette.primary20 }} isBold>{`${intl.formatMessage({
                id: `planoverview.route.table.header.total_distance`,
              })} (${activeProject?.units?.distance})`}</Text>
            ),
            formatter: ({ doc: driver }: { doc: Driver }) =>
              driver && activeProject?.units ? (
                <Text variant="body">
                  {kmToSM(
                    (driver.stops.reduce(
                      (prev, curr) => prev + (curr.distance_to_next_stop || 0),
                      driver.stops.length > 0 ? driver.stops[0].distance_to_previous_stop ?? 0 : 0
                    ) || 0) / 1000,
                    activeProject?.units?.distance
                  ).toFixed(1)}
                </Text>
              ) : null,
          });
          break;
        case `max_distance`:
          if (activeProject?.view?.constraints?.max_distance) {
            columns.push({
              width: 130,
              accessorName: `max_distance`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>{`${intl.formatMessage({
                  id: `planoverview.route.table.header.max_distance`,
                })} (${activeProject?.units?.distance})`}</Text>
              ),
              formatter: ({ value: maxDistance }) =>
                maxDistance && activeProject?.units ? (
                  <Text variant="body">{kmToSM(maxDistance / 1000, activeProject?.units?.distance).toFixed(1)}</Text>
                ) : null,
            });
          }
          break;
        case `min_distance`:
          if (activeProject?.view?.constraints?.min_distance && showRootParams) {
            columns.push({
              width: 130,
              accessorName: `min_distance`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>{`${intl.formatMessage({
                  id: `planoverview.route.table.header.min_distance`,
                })} (${activeProject?.units?.distance})`}</Text>
              ),
              formatter: ({ value: minDistance }) =>
                minDistance && activeProject?.units ? (
                  <Text variant="body">{kmToSM(minDistance / 1000, activeProject?.units?.distance).toFixed(1)}</Text>
                ) : null,
            });
          }
          break;
        case `max_time`:
          if (activeProject?.view?.constraints?.max_time) {
            columns.push({
              width: 130,
              accessorName: `max_time`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId="planoverview.route.table.header.max_time"
                />
              ),
              formatter: ({ value: maxTime }) => (maxTime ? <Text variant="body">{`${maxTime / 60} min`}</Text> : null),
            });
          }
          break;
        case `min_time`:
          if (activeProject?.view?.constraints?.min_time && showRootParams) {
            columns.push({
              width: 130,
              accessorName: `min_time`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId="planoverview.route.table.header.min_time"
                />
              ),
              formatter: ({ value: minTime }) => (minTime ? <Text variant="body">{`${minTime / 60} min`}</Text> : null),
            });
          }
          break;
        case `assigned_weight`:
          if (activeProject?.view?.constraints?.weight) {
            columns.push({
              width: 130,
              accessorName: `assigned_weight`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>{`${intl.formatMessage({
                  id: `planoverview.route.table.header.assigned_weight`,
                })} (${activeProject?.units?.weight})`}</Text>
              ),
              formatter: ({ doc }: { doc: Driver }) => (
                <Text variant="body">{Math.round(doc.getAssignedWeight() * 10) / 10}</Text>
              ),
            });
          }
          break;
        case `max_weight`:
          if (activeProject?.view?.constraints?.weight) {
            columns.push({
              width: 120,
              accessorName: `max_weight`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>{`${intl.formatMessage({
                  id: `planoverview.route.table.header.max_weight`,
                })} (${activeProject?.units?.weight})`}</Text>
              ),
              formatter: ({ value: maxWeight }) => <Text variant="body">{maxWeight ?? 0}</Text>,
            });
          }
          break;

        case `assigned_volume`:
          if (activeProject?.view?.constraints?.volume) {
            columns.push({
              width: 130,
              accessorName: `assigned_volume`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>{`${intl.formatMessage({
                  id: `planoverview.route.table.header.assigned_volume`,
                })} (${activeProject?.units?.volume})`}</Text>
              ),
              formatter: ({ doc }: { doc: Driver }) => (
                <Text variant="body">{Math.round(doc.getAssignedVolume() * 10) / 10}</Text>
              ),
            });
          }
          break;
        case `max_volume`:
          if (activeProject?.view?.constraints?.volume) {
            columns.push({
              width: 120,
              accessorName: `max_volume`,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>{`${intl.formatMessage({
                  id: `planoverview.route.table.header.max_volume`,
                })} (${activeProject?.units?.volume})`}</Text>
              ),
              formatter: ({ value: maxVolume }) => <Text variant="body">{maxVolume ?? 0}</Text>,
            });
          }
          break;
        case `total_price`:
          columns.push({
            width: 110,
            accessorName: `total_price`,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.total_price"
              />
            ),
            formatter: ({ doc }: { doc: Driver }) => (
              <Text variant="body">{doc.getTotalPriceWithUnits(activeProject)}</Text>
            ),
          });
          break;
        case `start_date`:
        case `end_date`:
          columns.push({
            width: 130,
            accessorName: identifier,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId={`planoverview.route.table.header.${identifier}`}
              />
            ),
            formatter: ({ value: date, doc: driver }) => {
              if (driver.status === DRIVER_STATUS.notStarted || !date) {
                return null;
              }
              return (
                <RoutalTooltip title={dateFormat(date)}>
                  <Text variant="body">{pastDateTime(date)}</Text>
                </RoutalTooltip>
              );
            },
          });
          break;
        case `planned_start_time`:
        case `planned_end_time`:
          columns.push({
            width: 130,
            accessorName: identifier,
            align: `center` as AlignmentTypes,
            sortable,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId={`planoverview.route.table.header.${identifier}`}
              />
            ),
            formatter: ({ value: time }) => {
              if (time === undefined) return <span>-</span>;
              return <Text variant="body">{parseSecondsToHM(time)}</Text>;
            },
          });
          break;
        case `provides`:
          columns.push({
            width: 220,
            align: `center` as AlignmentTypes,
            accessorName: `provides`,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.provides"
              />
            ),
            formatter: ({ doc: driver }) => {
              if (driver.provides && driver.provides.length > 0) {
                return driver.provides.length > 2 ? (
                  <Grid container alignItems="center" justifyContent="center">
                    <Tooltip disableHoverListener={driver.provides[0].length <= 8} title={driver.provides[0]}>
                      <Chip
                        key={driver.provides[0]}
                        label={
                          <Text variant="body">{`${driver.provides[0].substring(0, 8)}${
                            driver.provides[0].length > 8 ? `...` : ``
                          }`}</Text>
                        }
                      />
                    </Tooltip>
                    <span
                      style={{
                        cursor: `pointer`,
                        color: `#406867`,
                        fontFamily: `Geist Variable`,
                        marginLeft: `4px`,
                        border: `none`,
                      }}
                      onClick={(e) => {
                        if (onOpenRightPanel) {
                          onOpenRightPanel(e, driver);
                        }
                      }}
                    >
                      <Text tagName="span" intlId="view_more" />
                      ...
                    </span>
                  </Grid>
                ) : (
                  driver.provides.map((provide: string) => (
                    <Tooltip disableHoverListener={provide.length <= 8} title={provide}>
                      <Chip
                        key={provide}
                        label={
                          <Text variant="body">{`${provide.substring(0, 8)}${provide.length > 8 ? `...` : ``}`}</Text>
                        }
                      />
                    </Tooltip>
                  ))
                );
              }
              return null;
            },
          });
          break;
        case `external_id`:
        case `plate`:
        case `email`:
        case `phone`:
        case `comments`:
          if (activeProject?.view?.vehicle[identifier]) {
            columns.push({
              width: identifier === `external_id` ? 160 : 110,
              accessorName: identifier,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId={`planoverview.route.table.header.${identifier}`}
                />
              ),
              ...(identifier === `comments`
                ? {
                    formatter: ({ value: comments }) =>
                      comments && (
                        <Tooltip title={comments}>
                          <Text variant="body" style={{ maxWidth: `110px` }}>
                            {comments}
                          </Text>
                        </Tooltip>
                      ),
                  }
                : {}),
              ...(identifier === `phone`
                ? {
                    formatter: ({ value: phone }) => {
                      if (!phone) return undefined;

                      const parsedPhone = parsePhoneNumber(phone);
                      if (!parsedPhone) return undefined;

                      return <Text variant="body">{parsedPhone.formatInternational()}</Text>;
                    },
                  }
                : {}),
            });
          }
          break;
        case `distance_traveled`:
          if (showRootParams) {
            columns.push({
              width: 120,
              accessorName: identifier,
              align: `center` as AlignmentTypes,
              sortable,
              name: (
                <Text
                  style={{ color: RoutalPalette.primary20 }}
                  isBold
                  intlId="planoverview.route.table.header.distance_traveled"
                />
              ),
              formatter: ({ value: distanceTraveled }) =>
                distanceTraveled && <Text variant="body">{Math.round(distanceTraveled / 100) / 10} km</Text>,
            });
          }
          break;
        case `created_at`:
          break;
        default: {
          if (
            (identifier as DriversTableColumnKeys).includes(CUSTOM_FIELD_COLUMN_ID_PREFIX) &&
            identifier in driverCustomFieldDefinitionsMap
          ) {
            const customFieldDefinition = driverCustomFieldDefinitionsMap[identifier];
            columns.push({
              width: 160,
              accessorName: identifier,
              sortable,
              align: `center` as AlignmentTypes,
              name: (
                <Text style={{ color: RoutalPalette.primary20 }} isBold>
                  {customFieldDefinition.label}
                </Text>
              ),
              formatter: ({ doc }: { doc: Driver }) => {
                if (!doc?.custom_fields) {
                  return null;
                }
                const driverCustomFields = doc.custom_fields.value;
                const customField = driverCustomFields[customFieldDefinition.custom_id] as CustomFieldValues;

                return customFieldToTableValue({
                  doc,
                  customField,
                  customFieldDefinition,
                  onOpenRightPanel,
                });
              },
            });
          }
          break;
        }
      }
    }
  });

  return [
    ...columns,
    ...(tableColumns.find(({ displayable, identifier }) => identifier === `created_at` && displayable)
      ? [
          {
            width: 130,
            accessorName: `created_at`,
            align: `center` as AlignmentTypes,
            sortable: true,
            name: (
              <Text
                style={{ color: RoutalPalette.primary20 }}
                isBold
                intlId="planoverview.route.table.header.created_at"
              />
            ),
            formatter: ({ value: createdAt }) => (
              <RoutalTooltip title={dateFormat(createdAt)}>
                <Text variant="body">{pastDateTime(createdAt)}</Text>
              </RoutalTooltip>
            ),
          } as VirtualizedDataTableColumnProps,
        ]
      : []),
    {
      accessorName: `id`,
      align: `right`,
      width: 100,
      flex: 1,
      formatter: ({ doc: driver, rowHover }) => (
        <Grid container justifyContent="flex-end">
          {rowHover && (
            <DriversActionsMenu
              type="popover"
              planStops={planStops}
              planDrivers={planDrivers}
              activeProject={activeProject}
              drivers={[driver]}
              showViewDetails={false}
              popoverProps={{
                hideArrow: true,
                content: <More3Points />,
                style: { height: `18px`, color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
              }}
            />
          )}
        </Grid>
      ),
    },
  ] as Array<VirtualizedDataTableColumnProps>;
};
